import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  getChannelDefaults,
  poolOpeningDefaults,
} from '@/components/pages/job-posting/utils/channel-defaults';
import { computeChannelBudgets } from '@/components/pages/job-posting/utils/channel-utils';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { POOL } from '@/types/channels';

import { OpeningsList } from './openings-list';
import { Summary } from './summary';

interface OpeningSelectionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channelIcon: React.ReactNode;
  channelKey: string;
  channelSlug: string;
  displayTitle: string;
  recommendedBudget?: number;
  recommendedDuration?: number;
  isFree?: boolean;
  openingsSelectedForAnotherProduct: Set<string>;
}

export function OpeningSelectionSheet({
  open,
  onOpenChange,
  channelIcon,
  channelKey,
  channelSlug,
  displayTitle,
  recommendedBudget,
  recommendedDuration,
  isFree,
  openingsSelectedForAnotherProduct,
}: OpeningSelectionSheetProps) {
  const { hookForm, selectedOpeningIds } = useJobPostingContext();
  const { getValues, setValue } = hookForm;

  const [draftSelectedOpenings, setDraftSelectedOpenings] = useState<
    Record<string, number | null>
  >({});

  /**
   * Initialize the draft with previously selected openings when the sheet is opened
   * Pool has no budget — track selected openings as keys with null values.
   * Read from poolDetails.openingMessages (Pool's source of truth), not openingBudgets.
   * Other channels use openingBudgets.
   */
  useEffect(() => {
    if (!open) return;

    let previousBudgets: Record<string, number | null> = {};
    if (channelSlug === POOL) {
      const messages =
        getValues(`channels.${channelKey}.poolDetails.openingMessages`) ?? {};
      previousBudgets = Object.fromEntries(
        Object.keys(messages).map(id => [id, null]),
      );
    } else {
      previousBudgets =
        getValues(`channels.${channelKey}.openingBudgets`) ?? {};
    }

    if (Object.keys(previousBudgets).length > 0) {
      setDraftSelectedOpenings(previousBudgets);
    } else {
      const unsupported =
        getValues(`channels.${channelKey}.unsupportedOpenings`) ?? [];
      const defaultBudget =
        recommendedBudget ?? getChannelDefaults(channelSlug).budget ?? null;

      const draft: Record<string, number | null> = {};
      for (const id of selectedOpeningIds) {
        if (
          !unsupported.includes(id) &&
          !openingsSelectedForAnotherProduct.has(id)
        ) {
          draft[id] = defaultBudget;
        }
      }
      setDraftSelectedOpenings(draft);
    }
  }, [
    openingsSelectedForAnotherProduct,
    open,
    channelKey,
    channelSlug,
    getValues,
    recommendedBudget,
    selectedOpeningIds,
  ]);

  /* Drop the channel from the form state */
  const dropChannel = () => {
    const { [channelKey]: _drop, ...rest } = getValues('channels');
    setValue('channels', rest);
  };

  /**
   * Drop the channel from form state when it has zero committed openings.
   * Covers both cancel-on-new-channel and close-via-X/Escape/outside-click.
   * Pool tracks committed openings in poolDetails.openingMessages; others use openingBudgets.
   */
  const dropChannelIfEmpty = () => {
    const channels = getValues('channels') ?? {};
    const channel = channels[channelKey];
    if (!channel) return;

    const committedCount =
      channelSlug === POOL
        ? Object.keys(channel.poolDetails?.openingMessages ?? {}).length
        : Object.keys(channel.openingBudgets).length;

    if (committedCount === 0) dropChannel();
  };

  /* Handle the change of the open state of the sheet */
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      dropChannelIfEmpty();
    }
    onOpenChange(next);
  };

  /* Cancel the opening selection sheet. Drop the channel from the form state if it has no openings. */
  const handleCancel = () => {
    dropChannelIfEmpty();
    onOpenChange(false);
  };

  /**
   * Save the selected openings from draft state to the form state. Recalculate the channel budget and total channel budget.
   * If all openings are unselected, remove the channel from the form state.
   */
  const handleSave = () => {
    if (Object.keys(draftSelectedOpenings).length === 0) {
      dropChannel();
      onOpenChange(false);
      return;
    }

    // Read existing messages from form state and update the poolDetails.openingMessages
    if (channelSlug === POOL) {
      const existingMessages =
        getValues(`channels.${channelKey}.poolDetails.openingMessages`) ?? {};
      setValue(
        `channels.${channelKey}.poolDetails.openingMessages`,
        Object.fromEntries(
          Object.keys(draftSelectedOpenings).map(id => [
            id,
            existingMessages[id] ?? poolOpeningDefaults,
          ]),
        ),
      );
    }

    setValue(`channels.${channelKey}.openingBudgets`, draftSelectedOpenings);
    const { channelBudget, totalChannelBudget } = computeChannelBudgets({
      openingBudgets: draftSelectedOpenings,
      budgetType: getValues(`channels.${channelKey}.budgetType`),
      startsAt: getValues(`channels.${channelKey}.startsAt`),
      endAt: getValues(`channels.${channelKey}.endAt`),
    });
    setValue(`channels.${channelKey}.channelBudget`, channelBudget);
    setValue(`channels.${channelKey}.totalChannelBudget`, totalChannelBudget);

    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="min-w-[464px]">
        <SheetHeader>
          <SheetTitle>
            <FormattedMessage
              defaultMessage="Job board coverage"
              description="Title of the modal for selecting openings for a job board"
            />
          </SheetTitle>
        </SheetHeader>

        <OpeningsList
          channelIcon={channelIcon}
          channelKey={channelKey}
          displayTitle={displayTitle}
          recommendedBudget={recommendedBudget}
          draftSelectedOpenings={draftSelectedOpenings}
          onDraftChange={setDraftSelectedOpenings}
          openingsSelectedForAnotherProduct={openingsSelectedForAnotherProduct}
        />

        <div className="mt-auto">
          <Summary
            recommendedBudget={recommendedBudget}
            recommendedDuration={recommendedDuration}
            draftSelectedOpenings={draftSelectedOpenings}
            isFree={isFree}
          />

          {/* Cancel and Add buttons */}
          <div className="border-fx-neutral-200 flex items-center justify-between border-t p-2">
            <Button variant="secondary" type="button" onClick={handleCancel}>
              <FormattedMessage
                defaultMessage="Cancel"
                description="Label for the cancel button"
              />
            </Button>
            <Button variant="default" type="button" onClick={handleSave}>
              <FormattedMessage
                defaultMessage="Save"
                description="Label for the save button"
              />
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
